/**
 * Synonym dictionary for Care Manager shorthand and natural language.
 * Maps common abbreviations and phrases to the canonical terms used in the database.
 *
 * Keys are lowercase. Values are arrays of canonical terms that should match.
 * The search system expands queries using these mappings before running Fuse.
 */

// Abbreviation → canonical subcategory/specialty terms
const ABBREVIATIONS = {
  'snf':    ['Skilled Nursing Facility'],
  'al':     ['Assisted Living Facility'],
  'alf':    ['Assisted Living Facility'],
  'mc':     ['Memory Care', 'Memory Care (standalone)', "Alzheimer's / Dementia"],
  'ccrc':   ['CCRC'],
  'pt':     ['Physical Therapy', 'PT / OT / Speech Therapy'],
  'ot':     ['Occupational Therapy', 'PT / OT / Speech Therapy'],
  'slp':    ['Speech', 'PT / OT / Speech Therapy'],
  'ddd':    ['DDD'],
  'idd':    ['Group Home · DDD/IDD', 'Developmental Disability'],
  'dme':    ['DME / Medical Supply'],
  'vna':    ['Visiting Nurse / VNA'],
  'ltach':  ['LTACH'],
  'rhcf':   ['Boarding Home / RHCF'],
  'gcm':    ['Geriatric Care Manager (GCM/ALCA)'],
  'alca':   ['Geriatric Care Manager (GCM/ALCA)'],
  'mltss':  ['Medicaid / MLTSS'],
  'tbi':    ['Traumatic Brain Injury (TBI)'],
  'aba':    ['Autism / ABA'],
  'ent':    ['ENT'],
  'pmr':    ['Physiatry / PM&R'],
  'pm&r':   ['Physiatry / PM&R'],
  'ltc':    ['Skilled Nursing Facility'],
  'va':     ['Veterans / VA', 'Veterans Services'],
  'aps':    ['Adult Protective Services'],
  'gps':    ['GPS Tracking / Wandering'],
  'copd':   ['COPD / Pulmonary'],
  'cpa':    ['CPA / Accountant'],
}

// Natural language phrases → canonical terms
const PHRASES = {
  'nursing home':       ['Skilled Nursing Facility'],
  'nursing facility':   ['Skilled Nursing Facility'],
  'assisted living':    ['Assisted Living Facility'],
  'memory care':        ['Memory Care', 'Memory Care (standalone)', "Alzheimer's / Dementia"],
  'dementia care':      ["Alzheimer's / Dementia", 'Memory Care', 'Memory Care (standalone)'],
  'home care':          ['Home Care Agency'],
  'home health':        ['Home Care Agency', 'Visiting Nurse / VNA'],
  'adult daycare':      ['Adult Day Program'],
  'adult day':          ['Adult Day Program'],
  'day program':        ['Adult Day Program'],
  'group home':         ['Group Home · Mental Illness', 'Group Home · DDD/IDD'],
  'senior housing':     ['Independent Living / Senior Housing'],
  'senior living':      ['Independent Living / Senior Housing', 'Assisted Living Facility'],
  'independent living': ['Independent Living / Senior Housing'],
  'board and care':     ['Boarding Home / RHCF'],
  'boarding home':      ['Boarding Home / RHCF'],
  'elder law':          ['Elder Law Attorney'],
  'estate planning':    ['Estate Planning', 'Elder Law Attorney'],
  'financial planner':  ['Financial Planner / Advisor'],
  'financial advisor':  ['Financial Planner / Advisor'],
  'mental health':      ['Mental Health / Behavioral', 'Counseling / Mental Health', 'Psychiatric / Behavioral'],
  'behavioral health':  ['Mental Health / Behavioral', 'Psychiatric / Behavioral'],
  'wound care':         ['Wound Care'],
  'physical therapy':   ['Physical Therapy', 'PT / OT / Speech Therapy'],
  'occupational therapy': ['Occupational Therapy', 'PT / OT / Speech Therapy'],
  'speech therapy':     ['Speech', 'PT / OT / Speech Therapy'],
  'move management':    ['Moving / Senior Move Management'],
  'senior move':        ['Moving / Senior Move Management'],
  'wheelchair van':     ['Ambulette', 'Wheelchair'],
  'medical transport':  ['Medical Transport', 'Ambulance', 'Ambulette'],
  'non medical transport': ['Non-Medical Transport'],
  'visiting nurse':     ['Visiting Nurse / VNA'],
  'hospice care':       ['Hospice (Inpatient)', 'Hospice (Outpatient)'],
  'palliative care':    ['Palliative Care'],
  'respite care':       ['Respite'],
  'home modification':  ['Home Modification / Accessibility', 'Home Modification'],
  'stair lift':         ['Stair Lifts', 'Home Modification / Accessibility'],
  'medical supply':     ['DME / Medical Supply'],
  'medical supplies':   ['DME / Medical Supply'],
  'medical equipment':  ['DME / Medical Supply'],
  'brain injury':       ['Traumatic Brain Injury (TBI)'],
  'spinal cord':        ['Spinal Cord Injury'],
  'money management':   ['Payroll / Daily Money Management'],
  'daily money':        ['Payroll / Daily Money Management'],
  'trust officer':      ['Trust Officer / Trust Company'],
  'trust company':      ['Trust Officer / Trust Company'],
  'special needs':      ['Guardianship / Special Needs'],
  'caregiver support':  ['Caregiver Support', 'Respite'],
  'clean out':          ['Hauling / Cleanout'],
  'cleanout':           ['Hauling / Cleanout'],
  'junk removal':       ['Hauling / Cleanout'],
  'meal service':       ['Meal Service / Catering'],
  'meals on wheels':    ['Meal Service / Catering'],
  'real estate':        ['Home Appraisal / Real Estate'],
  'home appraisal':     ['Home Appraisal / Real Estate', 'Home Appraisals'],
  'estate sale':        ['Estate Sales'],
  'funeral':            ['Funeral Services'],
  'veteran services':   ['Veterans Services', 'Veterans / VA'],
  'veterans services':  ['Veterans Services', 'Veterans / VA'],
}

// Single-word synonyms → canonical terms
const WORD_SYNONYMS = {
  'attorney':     ['Elder Law Attorney'],
  'lawyer':       ['Elder Law Attorney'],
  'dentist':      ['Dentist / Dental'],
  'dental':       ['Dentist / Dental'],
  'pharmacy':     ['Pharmacy'],
  'pharmacist':   ['Pharmacy'],
  'psychiatrist': ['Psychiatry', 'Geriatric Psychiatry'],
  'psychiatry':   ['Psychiatry', 'Geriatric Psychiatry', 'Psychiatric / Behavioral'],
  'psychologist': ['Neuropsychology', 'Counseling / Mental Health'],
  'therapist':    ['Counseling / Mental Health'],
  'counselor':    ['Counseling / Mental Health'],
  'counseling':   ['Counseling / Mental Health'],
  'podiatrist':   ['Podiatry'],
  'podiatry':     ['Podiatry'],
  'neurologist':  ['Neurology'],
  'neurology':    ['Neurology'],
  'cardiologist': ['Cardiology'],
  'cardiology':   ['Cardiology'],
  'dermatologist':['Dermatology'],
  'dermatology':  ['Dermatology'],
  'urologist':    ['Urology'],
  'urology':      ['Urology'],
  'oncologist':   ['Oncology'],
  'oncology':     ['Oncology'],
  'ophthalmologist': ['Ophthalmology'],
  'ophthalmology':   ['Ophthalmology'],
  'optometrist':  ['Optometry'],
  'optometry':    ['Optometry'],
  'audiologist':  ['Audiology'],
  'audiology':    ['Audiology'],
  'handyman':     ['Home Improvement / Handyman', 'Handyman'],
  'plumber':      ['Plumbing'],
  'plumbing':     ['Plumbing'],
  'electrician':  ['Electrician'],
  'painter':      ['Painting'],
  'locksmith':    ['Locksmith'],
  'roofer':       ['Roofing'],
  'roofing':      ['Roofing'],
  'landscaper':   ['Landscaping'],
  'landscaping':  ['Landscaping'],
  'hvac':         ['HVAC'],
  'rehab':        ['Subacute Rehab', 'Acute Rehab', 'Rehabilitation'],
  'subacute':     ['Subacute Rehab'],
  'hospice':      ['Hospice (Inpatient)', 'Hospice (Outpatient)'],
  'respite':      ['Respite'],
  'dialysis':     ['Dialysis'],
  'dementia':     ["Alzheimer's / Dementia", 'Memory Care', 'Memory Care (standalone)'],
  'alzheimers':   ["Alzheimer's / Dementia"],
  "alzheimer's":  ["Alzheimer's / Dementia"],
  'parkinsons':   ["Parkinson's", 'Parkinsons'],
  "parkinson's":  ["Parkinson's"],
  'autism':       ['Autism / ABA'],
  'ambulance':    ['Ambulance'],
  'ambulette':    ['Ambulette'],
  'paratransit':  ['Paratransit'],
  'shuttle':      ['Shuttle'],
  'guardian':     ['Guardianship / Special Needs', 'Guardianship'],
  'guardianship': ['Guardianship / Special Needs', 'Guardianship', 'Court / Guardianship'],
  'insurance':    ['Insurance Specialist', 'LTC Insurance'],
  'accountant':   ['CPA / Accountant'],
  'moving':       ['Moving / Senior Move Management'],
  'mover':        ['Moving / Senior Move Management'],
  'movers':       ['Moving / Senior Move Management'],
  'organizing':   ['Professional Organizing'],
  'organizer':    ['Professional Organizing'],
  'hoarding':     ['Hoarding'],
  'ventilator':   ['Ventilator / Trach'],
  'vent':         ['Ventilator / Trach'],
  'trach':        ['Ventilator / Trach'],
  'incontinence': ['Incontinence'],
  'stroke':       ['Stroke'],
  'diabetes':     ['Diabetes'],
  'acupuncture':  ['Acupuncture'],
  'reiki':        ['Reiki'],
  'companion':    ['Companionship'],
  'companionship':['Companionship'],
  'errands':      ['Errands / Concierge', 'Concierge / Errands'],
  'concierge':    ['Errands / Concierge', 'Concierge / Errands'],
  'cleaning':     ['Cleaning Services'],
  'placement':    ['Placement Service'],
}

/**
 * Expand a search query by replacing abbreviations and phrases with canonical terms.
 * Returns { expandedTerms: string[], originalFreeText: string }
 *
 * expandedTerms: canonical database terms that matched synonyms
 * originalFreeText: whatever's left after extracting synonym matches (for fuzzy search)
 */
export function expandQuery(rawQuery) {
  let query = rawQuery.toLowerCase().trim()
  const expandedTerms = new Set()

  // 1. Check multi-word phrases first (longest match first)
  const phraseKeys = Object.keys(PHRASES).sort((a, b) => b.length - a.length)
  for (const phrase of phraseKeys) {
    if (query.includes(phrase)) {
      PHRASES[phrase].forEach(t => expandedTerms.add(t))
      query = query.replace(phrase, ' ').trim()
    }
  }

  // 2. Split remaining into words and check abbreviations + single-word synonyms
  const words = query.split(/\s+/).filter(Boolean)
  const remainingWords = []

  for (const word of words) {
    if (ABBREVIATIONS[word]) {
      ABBREVIATIONS[word].forEach(t => expandedTerms.add(t))
    } else if (WORD_SYNONYMS[word]) {
      WORD_SYNONYMS[word].forEach(t => expandedTerms.add(t))
    } else {
      remainingWords.push(word)
    }
  }

  return {
    expandedTerms: [...expandedTerms],
    originalFreeText: remainingWords.join(' ').trim(),
  }
}
